// Shared notification metadata + routing, used by NotificationView and the live toaster.

export const CATEGORIES = [
  { key: "social", label: "Followers & co-signs", desc: "New followers and co-signs" },
  { key: "messages", label: "Messages", desc: "New direct messages and message unlocks" },
  { key: "collab", label: "Collabs", desc: "Invites, applications and responses" },
  { key: "drops", label: "New releases", desc: "When artists you follow drop music" },
  { key: "rooms", label: "Live rooms", desc: "When artists you follow go live" },
  { key: "money", label: "Gifts & payouts", desc: "Gifts you receive and payouts sent to you" },
  { key: "milestones", label: "Milestones", desc: "Discovery-score milestones" },
];

// type → { icon, tone, category }.  category null = platform/account notices (always delivered).
export const TYPES = {
  follow: { icon: "👤", tone: "gold", category: "social" },
  cosign: { icon: "✅", tone: "teal", category: "social" },
  message: { icon: "💬", tone: "violet", category: "messages" },
  messageUnlock: { icon: "🔓", tone: "gold", category: "messages" },
  collab_invite: { icon: "🤝", tone: "violet", category: "collab" },
  collab_application: { icon: "🤝", tone: "violet", category: "collab" },
  collab_response: { icon: "🤝", tone: "violet", category: "collab" },
  application_accepted: { icon: "🎉", tone: "teal", category: "collab" },
  application_passed: { icon: "🤝", tone: "violet", category: "collab" },
  collab_project: { icon: "🎚", tone: "teal", category: "collab" },
  collab_split: { icon: "⚖️", tone: "gold", category: "collab" },
  collab_stage: { icon: "📦", tone: "violet", category: "collab" },
  collab_release: { icon: "🚀", tone: "teal", category: "collab" },
  collab_message: { icon: "💬", tone: "violet", category: "collab" },
  collab_payment: { icon: "💰", tone: "gold", category: "money" },
  drop: { icon: "🎵", tone: "gold", category: "drops" },
  room: { icon: "🎙", tone: "teal", category: "rooms" },
  gift: { icon: "🎁", tone: "gold", category: "money" },
  payout: { icon: "💸", tone: "teal", category: "money" },
  milestone: { icon: "⚡", tone: "teal", category: "milestones" },
  membership: { icon: "★", tone: "gold", category: null },
  paymentFailed: { icon: "⚠️", tone: "red", category: null },
  verified: { icon: "✔", tone: "teal", category: null },
  takedown: { icon: "🚫", tone: "red", category: null },
  roomEnded: { icon: "🎙", tone: "red", category: null },
  announcement: { icon: "📣", tone: "gold", category: null },
};

export const typeMeta = (type) => TYPES[type] || { icon: "🔔", tone: "gold", category: null };

export const FILTERS = [
  { key: "all", label: "All" },
  { key: "social", label: "Social" },
  { key: "messages", label: "Messages" },
  { key: "collab", label: "Collabs" },
  { key: "drops", label: "Releases" },
  { key: "rooms", label: "Live" },
  { key: "money", label: "Money" },
  { key: "milestones", label: "Milestones" },
  { key: "platform", label: "Muzilly" },
];

export const categoryOf = (n) => typeMeta(n.type).category || "platform";

/** user doc → "fan" | "artist" | "rising" */
export function roleOf(user) {
  const t = String((user && (user.type || user.role)) || "").toLowerCase();
  if (t === "rising") return "rising";
  if (["artist", "producer", "band", "label"].includes(t)) return "artist";
  return "fan";
}

export const ROLE_ROUTES = {
  fan: { home: "/fan-home", search: "/fan-discover", rooms: "/fan-rooms", messages: "/fan-messages", profile: "/fan-profile", membership: "/membership" },
  artist: { home: "/artist-home", search: "/artist-discover", drop: "/drop", rooms: "/artist-rooms", messages: "/artist-messages", collab: "/artist-collab", profile: "/artist-profile", analytics: "/artist-analytics", membership: "/membership" },
  rising: { home: "/rising-home", search: "/rising-discover", drop: "/drop", rooms: "/rising-rooms", messages: "/rising-messages", collab: "/rising-collab", profile: "/rising-profile", analytics: "/rising-analytics", membership: "/membership" },
};

/** notification → route path (or null when there's nowhere to go). */
export function resolveLink(notif, role) {
  const l = notif && notif.link;
  if (!l) return null;
  if (l.startsWith("artist:")) return "/artist/" + l.slice(7);
  if (l.startsWith("project:")) return "/collab-project/" + l.slice(8);
  const map = ROLE_ROUTES[role] || ROLE_ROUTES.fan;
  return map[l] || map.home;
}

export function timeAgo(ts) {
  try {
    if (!ts) return "Just now";
    const date = ts.toDate ? ts.toDate() : ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    if (isNaN(date)) return "";
    const diff = Math.floor((Date.now() - date) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return Math.floor(diff / 60) + "m ago";
    if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
    if (diff < 604800) return Math.floor(diff / 86400) + "d ago";
    return date.toLocaleDateString();
  } catch (e) { return ""; }
}

export const millisOf = (ts) => (!ts ? Date.now() : ts.toMillis ? ts.toMillis() : ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime() || 0);

export function dayBucket(ts) {
  const d = new Date(millisOf(ts));
  const startToday = new Date(); startToday.setHours(0, 0, 0, 0);
  const diff = Math.floor((startToday - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  return diff <= 0 ? "Today" : diff === 1 ? "Yesterday" : diff < 7 ? "This week" : "Earlier";
}

const COLORS = ["#2e5b7e", "#8b3a3a", "#6b3a5a", "#3a6b4d", "#7a5a2e", "#4a4a7e"];
export const colorFor = (name) => { let h = 0; for (const c of String(name || "?")) h = (h * 31 + c.charCodeAt(0)) >>> 0; return COLORS[h % COLORS.length]; };